import {
  debounce,
  differenceWith,
  groupBy,
  max,
  orderBy,
  sum,
  uniq,
  uniqBy
} from 'lodash';

import { usePromise } from '@issue-detail/utils/usePromise';
import { useRootInstance } from '@issue-detail/utils/useRootInstance';
import { waitForDefaultLayout } from '@issue-detail/utils/waitWithDefaultLayout';
import { computed, ref, Ref, watch } from '@vue/composition-api';
import { createEventHook } from '@vueuse/core';

import { getIssueLayout, getStickyTop, setStickyTop } from '../Api/issue';
import { IssueAssembleResponse, IssueFieldInfo } from '../Model/Issue';
import { ActionStore } from './action';
import { useFieldRenderScheduler } from './FieldRenderscheduler';
import { IssueTypeStore } from './issueType';
import { ProjectStore } from './project';
import { ShareStore } from './share';

const ColumnNumber = 6;
const PercentageToColumnFactor = 100 / ColumnNumber;

const hiddenFieldCodesOnVisitor = [
  'activities',
  'assignee',
  'activities',
  'comments',
  'follows',
  'owner',
  'createBy',
  'updateBy'
];

const hiddenControlCodesOnVisitor = ['member'];

export interface FieldLayout {
  rowNumber: number;
  columnNumber: number;
  fieldLayout: {
    code: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }[];
}

export const useLayout = (
  { action }: ActionStore,
  projectStore: ProjectStore,
  issueTypeStore: IssueTypeStore,
  issueAssemble: Ref<IssueAssembleResponse | null>,
  requestRefreshOptions: () => void,
  shareStore: ShareStore
) => {
  const fieldAreas = computed(() => issueAssemble.value?.issueAreaVOList || []);
  const instance = useRootInstance();

  const {
    result: stickyTopFieldCodesResponse,
    refresh: refreshStickyTop,
    isLoading: isLoadingStickyTop
  } = usePromise(
    async (project?: number, issueType?: number) =>
      project && issueType && !shareStore.isVisitor.value
        ? await getStickyTop(project, issueType)
        : [],
    [],
    { immediate: true }
  );

  const rightAreaStickyTopFieldCodes = computed(() =>
    stickyTopFieldCodesResponse.value
      .filter(({ type }) => type === 'top')
      .map(({ fieldCode }) => fieldCode)
  );

  const leftAreaStickyTopFieldCodes = computed(() =>
    stickyTopFieldCodesResponse.value
      .filter(({ type }) => type === 'tab_top')
      .map(({ fieldCode }) => fieldCode)
  );

  watch(
    [projectStore.currentProjectId, issueTypeStore.currentTypeId],
    ([project, type]) => {
      isLoadingStickyTop.value = true;
      refreshStickyTop(project, type);
    },
    { immediate: true }
  );

  const saveStickTopField = debounce(
    (
      codes: Array<{ fieldCode: string; type: 'top' | 'tab_top' }>,
      fields: IssueFieldInfo[],
      issueTypeId: number,
      projectId: number
    ) => {
      if (codes) {
        setStickyTop(
          projectId,
          issueTypeId,
          codes
            .map(
              ({ fieldCode, type }, index) =>
                ({
                  issueTypeId,
                  projectId,
                  fieldCode,
                  customFieldId:
                    fields.find((f) => f.fieldCode === fieldCode)
                      ?.customFieldId || 0,
                  ordinate: index,
                  areaName: type === 'top' ? 'right area' : 'left area',
                  type
                } as Parameters<typeof setStickyTop>[2][number])
            )
            .filter((item) => item.customFieldId)
        );
      }
    },
    2000
  );

  const stickTopField = (code: string, type: 'top' | 'tab_top') => {
    stickyTopFieldCodesResponse.value = uniqBy(
      [
        {
          type,
          fieldCode: code
        },
        ...stickyTopFieldCodesResponse.value
      ],
      (f) => f.fieldCode
    );
    instance.$track.send('Issue_FieldLabel', { type: code }, true);

    saveStickTopField(
      stickyTopFieldCodesResponse.value,
      allFields.value,
      issueTypeStore.currentTypeId.value!,
      projectStore.currentProjectId.value!
    );
  };
  const cancelStickTopField = (code: string) => {
    stickyTopFieldCodesResponse.value =
      stickyTopFieldCodesResponse.value.filter((c) => c.fieldCode !== code);
    saveStickTopField(
      stickyTopFieldCodesResponse.value,
      allFields.value,
      issueTypeStore.currentTypeId.value!,
      projectStore.currentProjectId.value!
    );
  };

  const isLoadingLayout = ref(false);
  const reloadLayout = async () => {
    if (
      !isLoadingLayout.value &&
      issueAssemble.value &&
      projectStore.currentProjectId.value &&
      issueTypeStore.currentTypeId.value
    ) {
      try {
        isLoadingLayout.value = true;
        const res = await getIssueLayout(
          projectStore.currentProjectId.value,
          issueTypeStore.currentTypeId.value
        );
        requestRefreshOptions();
        refreshStickyTop(
          projectStore.currentProjectId.value,
          issueTypeStore.currentTypeId.value
        );

        issueAssemble.value.issueAreaVOList = res;
      } finally {
        isLoadingLayout.value = false;
      }
    }
  };

  const getLayoutFromFields = (fields: IssueFieldInfo[]): FieldLayout => {
    const yGroups = groupBy(fields, (f) => f.ordinate);
    return {
      rowNumber: max(Object.keys(yGroups).map(Number)) || 0,
      columnNumber: ColumnNumber,
      fieldLayout: Object.entries(yGroups).flatMap(([y, fields]) =>
        fields.map((field) => ({
          code: field.fieldCode,
          x: Math.round((field.startPoint || 0) / PercentageToColumnFactor),
          y: Number(y),
          width: Math.round(field.width / PercentageToColumnFactor),
          height: 1
        }))
      )
    };
  };

  const getFieldsFromArea =
    (name: 'top area' | 'left area' | 'right area') => () =>
      fieldAreas.value
        .find((area) => area.areaName === name)
        ?.groupList.flatMap((group, _, groups) => {
          const preGroupsRowNumber = sum(
            groups
              .filter((g) => g.groupOrder < group.groupOrder)
              .map(
                (g) =>
                  uniq(g.issueFieldOrdinateVOList.map((f) => f.ordinate)).length
              )
          );

          return group.issueFieldOrdinateVOList
            .flatMap((g, o) =>
              g.issueFieldInfoVo.map((field) => ({
                ...field,
                ordinate: o + preGroupsRowNumber + 1
              }))
            )
            .filter((f) => f.showed === 1)
            .filter((f) =>
              shareStore.isVisitor.value
                ? !(
                  hiddenControlCodesOnVisitor.includes(f.controlCode) ||
                  hiddenFieldCodesOnVisitor.includes(f.fieldCode)
                )
                : true
            );
        }) || [];

  const topAreaFields = computed(getFieldsFromArea('top area'));

  const topAreaFieldCodes = computed(() =>
    topAreaFields.value.map((f) => f.fieldCode)
  );

  const leftAreaFields = computed(() => {
    const fields = getFieldsFromArea('left area')();
    return action.value?.type === 'Create' ||
      action.value?.type === 'PreviewType'
      ? fields.filter((f) => !['activities', 'comments'].includes(f.fieldCode))
      : fields;
  });
  const leftAreaLayout = computed(() =>
    getLayoutFromFields(leftAreaFields.value)
  );

  const rightAreaFields = computed(getFieldsFromArea('right area'));
  const rightAreaLayout = computed(() =>
    formatStickyTop(
      getLayoutFromFields(rightAreaFields.value),
      rightAreaStickyTopFieldCodes.value
    )
  );

  const formatStickyTop = (layout: FieldLayout, stickyFields: string[]) => {
    let cursor = 0;
    stickyFields
      .map((code) => layout.fieldLayout.find((f) => f.code === code)!)
      .filter(Boolean)
      .concat(
        orderBy(
          layout.fieldLayout.filter((f) => !stickyFields.includes(f.code)),
          (f) => f.y
        )
      )
      .forEach((field) => {
        if (stickyFields.includes(field.code)) {
          field.y = cursor;
          cursor++;
        } else {
          field.y += cursor;
        }
      });

    return layout;
  };

  const showFieldLayout = async () => {
    if (
      projectStore.currentProject.value &&
      issueTypeStore.currentIssueType.value &&
      issueAssemble.value
    ) {
      await waitForDefaultLayout(() =>
        document.querySelector<HTMLDivElement>(
          '#micro-app_project #app'
        )
      );
      instance.eventBus.$emit('config-field-layout:show', {
        issueType: issueTypeStore.currentIssueType.value,
        projectId: projectStore.currentProject.value!.id,
        projectName: projectStore.currentProject.value!.pname,
        projectColor: projectStore.currentProject.value!.backgroundColor
      });
      instance.$track.send('Issue_Adjustment', {}, true);
    }
  };

  const allFields = computed(() => [
    ...topAreaFields.value,
    ...leftAreaFields.value,
    ...rightAreaFields.value
  ]);

  const { restart, lazyRenderFields } = useFieldRenderScheduler();

  const fieldComponentMountedEvent = createEventHook<string>();

  return {
    isLoadingLayout,
    reloadLayout,
    showFieldLayout,
    fieldAreas,
    topAreaFields,
    topAreaFieldCodes,
    leftAreaFields,
    leftAreaLayout,
    rightAreaFields,
    rightAreaLayout,
    rightAreaStickyTopFieldCodes,
    allFields,
    stickTopField,
    leftAreaStickyTopFieldCodes,
    cancelStickTopField,
    isLoadingStickyTop,
    lazyRenderFields,
    startRenderLazyFields: restart,
    fireFieldComponentMounted: fieldComponentMountedEvent.trigger,
    onFieldComponentMounted: fieldComponentMountedEvent.on
  };
};

export type LayoutStore = ReturnType<typeof useLayout>;
